/**
 * Opening, closing and publishing a session — may the signed-in person?
 *
 * Mirrors `internal_session_publish_authority` (migration 704), the one
 * predicate `transition_session_state` and `publish_session_results` share:
 * the session's facilitator, its creator, the owner of its cycle, a workspace
 * admin or owner, a super admin, or a holder of `session.publish` or
 * `session.facilitate`. Admins hold every permission action on the client, so
 * `can()` covers the admin arms.
 *
 * This decides what a screen draws (build contract §2.8): the host controls
 * are drawn only for someone the acts will accept, and a plain line names who
 * that is for everyone else. The database stays the authority.
 */
import { useCycleStore } from '@/stores/prioritization/degapProcessStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { PermissionAction } from '@/types/prioritization/permissions';

export interface SessionPublishAuthorityInput {
  userId: string | null | undefined;
  facilitatorId: string | null | undefined;
  createdBy: string | null | undefined;
  /** `degap_cycles.owner_id` of the session's cycle, when it has one and it is loaded. */
  cycleOwnerId: string | null | undefined;
  can: (action: PermissionAction) => boolean;
}

export function sessionPublishAuthorityAllowed({
  userId,
  facilitatorId,
  createdBy,
  cycleOwnerId,
  can,
}: SessionPublishAuthorityInput): boolean {
  if (!userId) return false;
  if (facilitatorId && facilitatorId === userId) return true;
  if (createdBy && createdBy === userId) return true;
  if (cycleOwnerId && cycleOwnerId === userId) return true;
  return can('session.publish') || can('session.facilitate');
}

/**
 * The plain line drawn in place of the host controls. `what` is the thing in
 * the app's own words ("this allocation", "this offsite", "this session").
 */
export function sessionPublishAuthorityLine(what: string, hostName: string | null | undefined): string {
  const host = hostName ? `its host (${hostName})` : 'its host';
  return `Opening, closing and publishing ${what} takes ${host}, the cycle owner, a workspace admin, or someone with the Facilitate Sessions or Publish Session Results permission.`;
}

export interface SessionLike {
  facilitatorId?: string | null;
  createdBy?: string | null;
  cycleId?: string | null;
}

/** May the signed-in person open, close and publish this session? */
export function useSessionPublishAuthority(session: SessionLike | null | undefined): boolean {
  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  const cycleId = session?.cycleId ?? null;
  const cycleOwnerId = useCycleStore((s) => (cycleId ? s.getCycle(cycleId)?.ownerId ?? null : null));
  if (!session) return false;
  return sessionPublishAuthorityAllowed({
    userId,
    facilitatorId: session.facilitatorId,
    createdBy: session.createdBy,
    cycleOwnerId,
    can,
  });
}
